/**
 * Catálogo semilla y sus imágenes.
 *
 * Un producto es una RECETA, no un artículo: no tiene precio propio, tiene insumos
 * y minutos. Si sube el cobre se cambia un renglón de `dispositivos` y todas las
 * cotizaciones se recalculan solas.
 *
 * Los precios de Shelly son de Syscom, levantados el 2026-08-07. Son precio de
 * distribuidor y traen IVA incluido, por eso llevan `iva: true`: el motor los
 * normaliza a costo sin IVA antes de aplicar utilidades.
 *
 * `factor` es la porción del gasto fijo diario que absorbe cada producto. Los
 * valores actuales son PROVISIONALES y hay que calibrarlos con la operación real:
 * gobiernan el costo de operación y, por lo tanto, el UAFIR.
 */

/**
 * Las imágenes del catálogo viven en `graficos.js`, uno por categoría y uno por SKU.
 *
 * Antes se generaban aquí con un helper que ponía una figura blanca pequeña, centrada,
 * sobre un cuadro con degradado, y **repetía la misma figura entre productos distintos**:
 * «Salida de contacto» y «Contacto GFCI» compartían dibujo, igual que los tres productos
 * de automatización, y celosía con lambrín. Solo cambiaba el tono. Se sustituyó por
 * dibujos a sangre, uno por concepto y ninguno repetido.
 */
import { GRAFICO_CATEGORIA, GRAFICO_PRODUCTO } from './graficos.js'


export const CATEGORIAS = [
  { id: 'ilu', nombre: 'Iluminación',     img: GRAFICO_CATEGORIA.ilu },
  { id: 'cto', nombre: 'Contactos',       img: GRAFICO_CATEGORIA.cto },
  { id: 'tab', nombre: 'Tablero y carga', img: GRAFICO_CATEGORIA.tab },
  { id: 'seg', nombre: 'Seguridad',       img: GRAFICO_CATEGORIA.seg },
  { id: 'aut', nombre: 'Automatización',  img: GRAFICO_CATEGORIA.aut },
  { id: 'med', nombre: 'Medición',        img: GRAFICO_CATEGORIA.med },
  { id: 'arq', nombre: 'Arquitectónico',  img: GRAFICO_CATEGORIA.arq },
  { id: 'dia', nombre: 'Diagnóstico',     img: GRAFICO_CATEGORIA.dia }
]

/* El campo `tec` engancha un insumo con los motores de ingeniería: es lo que permite que
   `electrico.js` pida «un cable del 12 en THW» y encuentre ÉSTE, con el precio que Juan
   le haya puesto. Ver la cabecera de `catalogo-tecnico.js`, que trae el resto de la
   familia. Estos dos ya existían antes de esa costura y por eso viven aquí: se les agregó
   `tec` en vez de duplicarlos, para que siga habiendo un solo cable del 12 en el sistema. */
export const DISPOSITIVOS = [
  { sku: 'CBL-12',  nombre: 'Cable THW cal. 12 (m)',        tipo: 'Consumibles',  costo: 22,
    tec: { clase: 'cable', awg: '12', aislamiento: 'thw' } },
  { sku: 'CBL-10',  nombre: 'Cable THW cal. 10 (m)',        tipo: 'Consumibles',  costo: 34,
    tec: { clase: 'cable', awg: '10', aislamiento: 'thw' } },
  { sku: 'TUBO-13', nombre: 'Poliducto 13 mm (m)',          tipo: 'Consumibles',  costo: 9.5 },
  { sku: 'CAJA-CH', nombre: 'Caja chalupa galvanizada',     tipo: 'Consumibles',  costo: 18 },
  { sku: 'PLACA-1', nombre: 'Placa y accesorio',            tipo: 'Complementos', costo: 78 },
  { sku: 'PAST-1P', nombre: 'Termomagnético 1 polo',        tipo: 'Complementos', costo: 210 },
  { sku: 'DIF-2P',  nombre: 'Diferencial 2 polos',          tipo: 'Complementos', costo: 1250 },
  { sku: 'VARILLA', nombre: 'Varilla Copperweld 5/8 x 3 m', tipo: 'Complementos', costo: 520 },
  { sku: 'CONECT',  nombre: 'Conector y registro de tierra',tipo: 'Complementos', costo: 340 },
  { sku: 'CENTRO',  nombre: 'Centro de carga 8 circuitos',  tipo: 'Complementos', costo: 1180 },
  { sku: 'SHELLYPRO1',   nombre: 'Shelly Pro 1',            tipo: 'Complementos', costo: 1116.97, iva: true },
  { sku: 'SHELLYPRO4PM', nombre: 'Shelly Pro 4PM',          tipo: 'Complementos', costo: 2223.62, iva: true },
  { sku: 'SHELLYDIM',    nombre: 'Shelly Pro Dimmer 1PM',   tipo: 'Complementos', costo: 1012.23, iva: true },
  { sku: 'SHELLY3EM',    nombre: 'Shelly Pro 3EM trifásico',tipo: 'Complementos', costo: 2695.53, iva: true }
]

/**
 * Lo que cada producto LE DICE AL CLIENTE — sale impreso en la propuesta, bajo su
 * renglón (pedido de Juan, 2026-08-18: «un documento donde explique al cliente qué
 * hace cada cosa cotizada, y que apoye a la venta»). Lenguaje del cliente de 55 a
 * 75 años: qué hace y por qué le importa, sin jerga y sin promesas que no se
 * puedan sostener. Editable por producto en Configuración → Productos.
 */
const EXPLICA = {
  'SAL-CTO':   'Un punto de corriente nuevo donde la casa lo necesita, cableado y ' +
               'entubado desde el tablero — se acabaron las extensiones y los ' +
               'multicontactos calientes.',
  'SAL-CTO-P': 'Contacto con protección contra electrocución: corta la corriente en ' +
               'milésimas de segundo si detecta una fuga. Es el indicado junto al ' +
               'agua — baño, cocina, exterior.',
  'SAL-ILU':   'Un punto de luz nuevo con su apagador, cableado desde el circuito ' +
               'correcto y dentro de la pared — no un cable sobrepuesto.',
  'SAL-ESC':   'Dos apagadores para la misma luz: se enciende abajo y se apaga ' +
               'arriba. Fin de subir la escalera a oscuras.',
  'CTO-DED':   'Una línea exclusiva desde el tablero para un aparato exigente — ' +
               'horno, minisplit, bomba—: no comparte con nada, no calienta y no ' +
               'bota pastillas.',
  'CENTRO-C':  'Tablero nuevo con espacio de sobra: cada circuito con su protección ' +
               'correcta y lugar para crecer mañana sin volver a abrir la pared.',
  'TIERRA':    'El camino que una falla eléctrica necesita para irse al suelo y no a ' +
               'las personas ni a los aparatos. Protege la electrónica de la casa — ' +
               'y las aseguradoras la piden como condición para cubrir equipos.',
  'DIFEREN':   'Vigila que la corriente que entra sea la misma que regresa; si algo ' +
               'se fuga —un cable dañado, un aparato mojado— corta antes de que ' +
               'lastime a alguien.',
  'SH-LUZ':    'La luz de siempre, ahora también desde el teléfono y con horarios. ' +
               'El apagador de la pared sigue funcionando exactamente igual.',
  'SH-DIM':    'Luz que sube y baja de intensidad: cena, lectura o limpieza con la ' +
               'misma lámpara — y menos consumo cuando no hace falta el cien por ciento.',
  'SH-TAB4':   'Cuatro circuitos de la casa medidos y controlables desde el ' +
               'teléfono: qué está encendido, cuánto consume, y apagado remoto.',
  'SH-MED':    'El medidor propio de la casa: cuánta luz se consume, cuándo y en ' +
               'qué. Con datos, el recibo de CFE deja de ser una sorpresa.',
  'TZ-CEL':    'Celosía de fachada cortada a diseño en nuestro taller: privacidad y ' +
               'sombra sin perder luz ni ventilación.',
  'TZ-LAM':    'Lambrín de madera cortado a medida en nuestro taller: la pared ' +
               'principal con acabado de mueble, no de tablarroca.',
  'REVISION':  'Mediciones con instrumentos —aislamiento, termografía, carga por ' +
               'circuito y tierra física— y un dictamen en su mano el mismo día, ' +
               'con precio cerrado de lo que haya que corregir. Se mide antes de proponer.'
}

export const PRODUCTOS = [
  { sku:'SAL-CTO',  nombre:'Salida de contacto',        cat:'cto', tIns:38,  tCfg:0,  precio:0,    factor:0.08,
    comp:[{sku:'CBL-12',c:9},{sku:'TUBO-13',c:5},{sku:'CAJA-CH',c:1},{sku:'PLACA-1',c:1}], img: GRAFICO_PRODUCTO['SAL-CTO'] },
  { sku:'SAL-CTO-P',nombre:'Contacto GFCI',             cat:'cto', tIns:46,  tCfg:0,  precio:0,    factor:0.10,
    comp:[{sku:'CBL-12',c:9},{sku:'TUBO-13',c:5},{sku:'CAJA-CH',c:1},{sku:'PLACA-1',c:1},{sku:'PAST-1P',c:1}], img: GRAFICO_PRODUCTO['SAL-CTO-P'] },
  { sku:'SAL-ILU',  nombre:'Salida de iluminación',     cat:'ilu', tIns:34,  tCfg:0,  precio:0,    factor:0.07,
    comp:[{sku:'CBL-12',c:7},{sku:'TUBO-13',c:4},{sku:'CAJA-CH',c:1},{sku:'PLACA-1',c:1}], img: GRAFICO_PRODUCTO['SAL-ILU'] },
  { sku:'SAL-ESC',  nombre:'Apagador de escalera',      cat:'ilu', tIns:52,  tCfg:0,  precio:0,    factor:0.11,
    comp:[{sku:'CBL-12',c:14},{sku:'TUBO-13',c:8},{sku:'CAJA-CH',c:2},{sku:'PLACA-1',c:2}], img: GRAFICO_PRODUCTO['SAL-ESC'] },
  { sku:'CTO-DED',  nombre:'Circuito dedicado 20A',     cat:'tab', tIns:95,  tCfg:0,  precio:0,    factor:0.20,
    comp:[{sku:'CBL-10',c:16},{sku:'TUBO-13',c:9},{sku:'PAST-1P',c:1}], img: GRAFICO_PRODUCTO['CTO-DED'] },
  { sku:'CENTRO-C', nombre:'Centro de carga nuevo',     cat:'tab', tIns:150, tCfg:20, precio:0,    factor:0.35,
    comp:[{sku:'CENTRO',c:1},{sku:'CBL-10',c:8}], img: GRAFICO_PRODUCTO['CENTRO-C'] },
  { sku:'TIERRA',   nombre:'Tierra física con registro',cat:'seg', tIns:180, tCfg:0,  precio:0,    factor:0.40,
    comp:[{sku:'VARILLA',c:1},{sku:'CONECT',c:1},{sku:'CBL-10',c:12}], img: GRAFICO_PRODUCTO['TIERRA'] },
  { sku:'DIFEREN',  nombre:'Protección diferencial',    cat:'seg', tIns:55,  tCfg:15, precio:0,    factor:0.14,
    comp:[{sku:'DIF-2P',c:1}], img: GRAFICO_PRODUCTO['DIFEREN'] },
  { sku:'SH-LUZ',   nombre:'Punto de luz automatizado', cat:'aut', tIns:40,  tCfg:20, precio:0,    factor:0.12,
    comp:[{sku:'SHELLYPRO1',c:1}], img: GRAFICO_PRODUCTO['SH-LUZ'] },
  { sku:'SH-DIM',   nombre:'Punto atenuable',           cat:'aut', tIns:45,  tCfg:25, precio:0,    factor:0.13,
    comp:[{sku:'SHELLYDIM',c:1}], img: GRAFICO_PRODUCTO['SH-DIM'] },
  { sku:'SH-TAB4',  nombre:'Tablero de 4 circuitos',    cat:'aut', tIns:75,  tCfg:45, precio:0,    factor:0.22,
    comp:[{sku:'SHELLYPRO4PM',c:1}], img: GRAFICO_PRODUCTO['SH-TAB4'] },
  { sku:'SH-MED',   nombre:'Medición de consumo',       cat:'med', tIns:60,  tCfg:35, precio:0,    factor:0.18,
    comp:[{sku:'SHELLY3EM',c:1}], img: GRAFICO_PRODUCTO['SH-MED'] },
  { sku:'TZ-CEL',   nombre:'Celosía SAT (m²)',          cat:'arq', tIns:55,  tCfg:0,  precio:4900, factor:0.25, comp:[], img: GRAFICO_PRODUCTO['TZ-CEL'] },
  { sku:'TZ-LAM',   nombre:'Lambrín NOG (m²)',          cat:'arq', tIns:40,  tCfg:0,  precio:2400, factor:0.18, comp:[], img: GRAFICO_PRODUCTO['TZ-LAM'] },
  { sku:'REVISION', nombre:'Revisión de seguridad',     cat:'dia', tIns:0,   tCfg:0,  precio:600,  factor:0.05, comp:[], img: GRAFICO_PRODUCTO['REVISION'] }
].map(p => ({ ...p, explica: EXPLICA[p.sku] || '' }))

export const PAQUETES = [
  { id:'pk1', nombre:'Recámara básica',   det:'4 contactos, 2 salidas de luz',      items:[{sku:'SAL-CTO',q:4},{sku:'SAL-ILU',q:2}] },
  { id:'pk2', nombre:'Paquete Raíz',      det:'Tierra, diferencial y centro de carga', items:[{sku:'TIERRA',q:1},{sku:'DIFEREN',q:1},{sku:'CENTRO-C',q:1}] },
  { id:'pk3', nombre:'Cocina segura',     det:'2 circuitos dedicados, 3 GFCI',      items:[{sku:'CTO-DED',q:2},{sku:'SAL-CTO-P',q:3}] },
  { id:'pk4', nombre:'Sala automatizada', det:'3 puntos de luz, 1 atenuable',       items:[{sku:'SH-LUZ',q:3},{sku:'SH-DIM',q:1}] }
]

/** Canales de venta. Definen la logística base y el reparto de comisión. */
export const EQUIPOS = [
  { id:'eq1', nombre:'Directo — Naucalpan', distancia:12, tiempo:35,  proporcionDeComision:5,  distribuidor:false },
  { id:'eq2', nombre:'Directo — Foráneo',   distancia:85, tiempo:110, proporcionDeComision:5,  distribuidor:false },
  { id:'eq3', nombre:'Canal — Arquitecto',  distancia:12, tiempo:35,  proporcionDeComision:12, distribuidor:true }
]
